/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Real-Time TM Suggestion, Glossary & QA Linter Drawer (Task 10.5.2)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  Database,
  Sparkles,
  BookOpen,
  ShieldAlert,
  CheckCircle2,
  Clock,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import { useCatEditorStore, CatSegment } from '../../stores/useCatEditorStore';
import { DiffViewer } from './DiffViewer';
import { GlossaryCard } from './GlossaryCard';
import { QaWarningList } from './QaWarningList';

export type DrawerTab = 'tm' | 'mt' | 'glossary' | 'qa';

interface TmSuggestionDrawerProps {
  segment: CatSegment | undefined;
  onApplySuggestion: (id: string, type: 'tm' | 'mt') => void;
  onInsertText: (text: string) => void;
  className?: string;
}

export const TmSuggestionDrawer: React.FC<TmSuggestionDrawerProps> = ({
  segment,
  onApplySuggestion,
  onInsertText,
  className = '',
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<DrawerTab>('tm');
  const { glossary, getMatchedGlossaryTerms } = useCatEditorStore();

  if (!segment) {
    return (
      <div className={`p-6 bg-surface border border-surface-border rounded-xl flex flex-col items-center justify-center text-center text-xs text-foreground-muted space-y-2 ${className}`}>
        <Database className="size-8 text-foreground-muted/40" />
        <p className="font-medium text-foreground">{t('cat.noActiveSegment')}</p>
        <p className="text-[11px] max-w-xs">{t('cat.selectSegmentHint')}</p>
      </div>
    );
  }

  const matchedTerms = getMatchedGlossaryTerms(segment.sourceText);
  const qaCount = segment.qaWarnings.length;

  return (
    <div className={`flex flex-col bg-surface border border-surface-border rounded-xl shadow-sm overflow-hidden ${className}`}>
      {/* Top Header & Tab Navigation */}
      <div className="border-b border-surface-border bg-surface-elevated/40">
        <div className="px-4 py-2.5 flex items-center justify-between border-b border-surface-border/60">
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs font-bold text-primary">
              #{segment.chunkIndex}
            </span>
            <span className="text-xs text-foreground font-medium truncate max-w-[200px]">
              {segment.sourceText}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {segment.tmMatchPercent > 0 && (
              <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500 border border-emerald-500/30">
                TM {segment.tmMatchPercent}%
              </span>
            )}
            {qaCount > 0 && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500 border border-amber-500/30 flex items-center gap-1">
                <ShieldAlert className="size-3" />
                {qaCount}
              </span>
            )}
          </div>
        </div>

        {/* Tab Buttons */}
        <div className="flex items-center px-2 overflow-x-auto">
          <button
            type="button"
            onClick={() => setActiveTab('tm')}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'tm'
                ? 'border-primary text-primary'
                : 'border-transparent text-foreground-muted hover:text-foreground'
            }`}
          >
            <Database className="size-3.5 text-emerald-500" />
            <span>{t('cat.tabTm')}</span>
            {segment.tmSuggestion && (
              <span className="text-[10px] px-1 rounded-full bg-emerald-500/15 text-emerald-500 font-mono">
                {segment.tmSuggestion.matchPercent}%
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('mt')}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'mt'
                ? 'border-primary text-primary'
                : 'border-transparent text-foreground-muted hover:text-foreground'
            }`}
          >
            <Sparkles className="size-3.5 text-primary" />
            <span>{t('cat.tabMt')}</span>
            <span className="text-[10px] px-1 rounded-full bg-primary/15 text-primary">
              AI
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('glossary')}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'glossary'
                ? 'border-primary text-primary'
                : 'border-transparent text-foreground-muted hover:text-foreground'
            }`}
          >
            <BookOpen className="size-3.5 text-primary" />
            <span>{t('cat.tabGlossary')}</span>
            {matchedTerms.length > 0 && (
              <span className="text-[10px] px-1.5 rounded-full bg-primary/15 text-primary font-bold">
                {matchedTerms.length}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('qa')}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border-b-2 transition-colors whitespace-nowrap ${
              activeTab === 'qa'
                ? 'border-primary text-primary'
                : 'border-transparent text-foreground-muted hover:text-foreground'
            }`}
          >
            <ShieldAlert className="size-3.5 text-amber-500" />
            <span>{t('cat.tabQa')}</span>
            {qaCount > 0 ? (
              <span className="text-[10px] px-1.5 rounded-full bg-amber-500/20 text-amber-500 font-bold">
                {qaCount}
              </span>
            ) : (
              <CheckCircle2 className="size-3 text-emerald-500" />
            )}
          </button>
        </div>
      </div>

      {/* Drawer Body Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[380px]">
        {/* 1. TM MATCHES TAB */}
        {activeTab === 'tm' && (
          <div className="space-y-3">
            {segment.tmSuggestion ? (
              <div className="p-3.5 rounded-xl border border-surface-border bg-surface-elevated/40 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-xs font-bold font-mono px-2 py-0.5 rounded-full border ${
                        segment.tmSuggestion.matchPercent === 100
                          ? 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30'
                          : 'bg-amber-500/15 text-amber-500 border-amber-500/30'
                      }`}
                    >
                      {segment.tmSuggestion.matchPercent === 100
                        ? '100% Exact Match'
                        : `${segment.tmSuggestion.matchPercent}% Fuzzy Match`}
                    </span>
                    <span className="text-[10px] text-foreground-muted flex items-center gap-1">
                      <Clock className="size-3" />
                      <span>Ledger: D1 TM Engine</span>
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => onApplySuggestion(segment.id, 'tm')}
                    className="px-2.5 py-1 rounded-lg bg-emerald-500 text-white font-semibold text-xs flex items-center gap-1.5 shadow-sm hover:bg-emerald-600 transition-colors"
                    title={t('cat.shortcutInsertTmDesc')}
                  >
                    <span>{t('cat.applyMatch')}</span>
                    <kbd className="font-mono text-[9px] bg-black/20 px-1 py-0.2 rounded">
                      Ctrl+1
                    </kbd>
                  </button>
                </div>

                {/* Diff Viewer between TM Source vs Segment Source */}
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-[11px] text-foreground-muted">
                    <span className="font-medium">{t('cat.diffViewerLabel')}:</span>
                    <span className="text-[10px]">
                      <span className="text-emerald-500">+ Added</span> /{' '}
                      <span className="text-rose-500 line-through">- Removed</span>
                    </span>
                  </div>
                  <DiffViewer
                    tmSource={segment.tmSuggestion.sourcePhrase || segment.sourceText}
                    currentSource={segment.sourceText}
                  />
                </div>

                {/* TM Target Translation */}
                <div className="space-y-1">
                  <span className="text-[11px] font-medium text-foreground-muted">
                    {t('cat.tmTargetLabel')}:
                  </span>
                  <div className="p-2.5 rounded-lg bg-surface border border-surface-border text-xs text-foreground font-sans leading-relaxed select-text">
                    {segment.tmSuggestion.text}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 rounded-xl border border-dashed border-surface-border text-center text-xs text-foreground-muted space-y-1.5">
                <Database className="size-6 text-foreground-muted/40 mx-auto" />
                <p className="font-medium text-foreground">{t('cat.noTmMatchTitle')}</p>
                <p className="text-[11px]">{t('cat.noTmMatchDesc')}</p>
              </div>
            )}
          </div>
        )}

        {/* 2. MACHINE TRANSLATION TAB */}
        {activeTab === 'mt' && (
          <div className="space-y-3">
            {segment.mtSuggestion ? (
              <div className="p-3.5 rounded-xl border border-surface-border bg-surface-elevated/40 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary/15 text-primary border border-primary/25 flex items-center gap-1">
                      <Sparkles className="size-3" />
                      {segment.mtSuggestion.provider}
                    </span>
                    <span className="text-[10px] text-foreground-muted">
                      Cloudflare AI Gateway
                    </span>
                  </div>

                  <button
                    type="button"
                    onClick={() => onApplySuggestion(segment.id, 'mt')}
                    className="px-2.5 py-1 rounded-lg bg-primary text-primary-fg font-semibold text-xs flex items-center gap-1.5 shadow-sm hover:opacity-95 transition-all"
                    title={t('cat.shortcutInsertMtDesc')}
                  >
                    <span>{t('cat.applyMt')}</span>
                    <kbd className="font-mono text-[9px] bg-black/20 px-1 py-0.2 rounded">
                      Ctrl+2
                    </kbd>
                  </button>
                </div>

                <div className="space-y-1">
                  <span className="text-[11px] font-medium text-foreground-muted">
                    {t('cat.mtOutputLabel')}:
                  </span>
                  <div className="p-2.5 rounded-lg bg-surface border border-surface-border text-xs text-foreground font-sans leading-relaxed select-text">
                    {segment.mtSuggestion.text}
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 rounded-xl border border-dashed border-surface-border text-center text-xs text-foreground-muted space-y-1.5">
                <Sparkles className="size-6 text-foreground-muted/40 mx-auto" />
                <p className="font-medium text-foreground">{t('cat.noMtTitle')}</p>
                <p className="text-[11px]">{t('cat.noMtDesc')}</p>
              </div>
            )}
          </div>
        )}

        {/* 3. GLOSSARY / TERMBASE TAB */}
        {activeTab === 'glossary' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-xs text-foreground-muted">
              <span className="font-medium">
                {matchedTerms.length} {t('cat.termsFoundInSegment')}
              </span>
              <span className="text-[11px]">
                {glossary.length} {t('cat.totalGlossaryTerms')}
              </span>
            </div>

            {matchedTerms.length > 0 ? (
              <div className="space-y-2">
                {matchedTerms.map((term) => (
                  <GlossaryCard
                    key={term.id}
                    term={term}
                    onInsertTerm={(text) => onInsertText(text)}
                    isUsedInTarget={segment.targetText.toLowerCase().includes(term.targetTerm.toLowerCase())}
                  />
                ))}
              </div>
            ) : (
              <div className="p-6 rounded-xl border border-dashed border-surface-border text-center text-xs text-foreground-muted space-y-1.5">
                <BookOpen className="size-6 text-foreground-muted/40 mx-auto" />
                <p className="font-medium text-foreground">{t('cat.noTermsInSegment')}</p>
                <p className="text-[11px]">{t('cat.noTermsInSegmentDesc')}</p>
              </div>
            )}
          </div>
        )}

        {/* 4. REAL-TIME QA LINTER TAB */}
        {activeTab === 'qa' && (
          <QaWarningList
            warnings={segment.qaWarnings}
            targetText={segment.targetText}
            sourceText={segment.sourceText}
          />
        )}
      </div>
    </div>
  );
};
